import { useState } from "react";
import { AlertTriangle, BadgePercent, Check, MessageCircle, Send, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../context/LanguageContext";
import { api, errorMessage, TELEGRAM, whatsappLink } from "../../lib/api";
import { CopyCode } from "../CopyCode";

export const STUDENT_FIELDS = (t) => [
  ["full_name", t.reg.name, "text", "Dr. Ahmed Mohammed", true, "full"],
  ["specialty", t.reg.specialty, "text", "e.g. Cardiology, Internal Medicine...", true, "full"],
  ["email", t.reg.email, "email", "email@example.com", true, "full"],
  ["phone", t.reg.phone, "tel", "+966 5XXXXXXXX", true, "full", t.reg.phoneHint],
  ["institution", t.reg.institution, "text", "e.g. King Faisal Specialist Hospital...", false, "full"],
  ["country", t.reg.country, "text", "e.g. Saudi Arabia", false, ""],
  ["city", t.reg.city, "text", "e.g. Riyadh", false, ""],
  ["orcid", t.reg.orcid, "text", "0000-0001-2345-6789", false, "full"],
];

/** Shared student fields; `values` enables edit mode (prefilled). */
export const StudentFields = ({ values = {}, showDiscount = true }) => {
  const { t } = useLang();
  const [discount, setDiscount] = useState(null);
  const check = async (e) => {
    const code = e.target.value.trim();
    if (!code) return setDiscount(null);
    try { const r = await api.get(`/discounts/validate/${encodeURIComponent(code)}`); setDiscount({ ok: true, ...r.data }); }
    catch { setDiscount({ ok: false }); }
  };
  return (<>
    {STUDENT_FIELDS(t).map(([name, label, type, ph, req, cls, hint]) => (
      <label className={cls} key={name}><span>{label}{req && <span className="req"> *</span>}</span>
        <input name={name} type={type} placeholder={ph} required={req} defaultValue={values[name] || ""} pattern={name === "phone" ? "^\\+?[0-9\\s\\-]{9,16}$" : undefined} data-testid={`reg-${name.replace("_", "-")}-input`} />
        {hint && <span className="hint">{hint}</span>}
      </label>))}
    {showDiscount && <label className="full"><span><BadgePercent size={14} style={{ verticalAlign: -2 }} /> {t.reg.discount}</span>
      <input name="discount_code" placeholder="PROMO2026" defaultValue={values.discount_code || ""} onBlur={check} className="en" style={{ textTransform: "uppercase", letterSpacing: ".08em" }} data-testid="reg-discount-input" />
      {discount?.ok && <span className="hint" style={{ color: "var(--primary)", fontWeight: 700 }} data-testid="discount-valid">{t.reg.discountOk} {discount.percent}%</span>}
      {discount && !discount.ok && <span className="hint" style={{ color: "var(--danger)" }} data-testid="discount-invalid">{t.reg.discountBad}</span>}
    </label>}
  </>);
};

/** Key facts of an opportunity (price, journal, seats...) shown before the form so applicants know what they register for. */
export const OpportunitySummary = ({ opp }) => {
  const { t, rtl } = useLang();
  const facts = [[t.opps.price, opp.price_note], [t.opps.journal, opp.journal_target], [t.opps.stage, opp.stage], [t.opps.deadline, opp.deadline], [t.opps.lead, opp.lead_researcher], [t.opps.type, opp.study_type]].filter(([, v]) => v);
  return (
    <div className="opp-summary" data-testid="opportunity-summary">
      <div className="opp-summary-head">
        <span className="badge badge-indigo">{rtl ? opp.specialty_ar : opp.specialty_en}</span>
        <span className="badge badge-green">{t.reg.seats}: {opp.remaining_seats} / {opp.total_seats}</span>
      </div>
      <h3 className="en">{opp.title_en}</h3>
      {opp.title_ar && <p className="opp-summary-ar">{opp.title_ar}</p>}
      {facts.length > 0 && <div className="facts" style={{ marginTop: 12 }}>{facts.map(([k, v]) => <div className="fact" key={k}><small>{k}</small><b>{v}</b></div>)}</div>}
      {opp.description && <p className="opp-summary-desc">{opp.description}</p>}
    </div>
  );
};

export const RegistrationForm = ({ opp, onDone, endpoint = "/registrations", headers, compact = false }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const approvalMessage = (data) => `السلام عليكم، سجّلت في فرصة بحثية وأرغب باعتماد تسجيلي:\n• الفرصة: ${opp.title_en}\n• التخصص: ${opp.specialty_ar}\n• الاسم: ${data.full_name}\n• التخصص الدقيق: ${data.specialty}\n• رقم الواتساب: ${data.phone}\n• البريد: ${data.email}${data.discount_code ? `\n• كود الخصم: ${data.discount_code}` : ""}`;

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const form = e.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());
    // Open the WhatsApp tab synchronously inside the click so mobile/desktop popup blockers allow it;
    // its address is set once the registration is saved. Coordinator (compact) mode never opens WhatsApp.
    const waTab = compact ? null : window.open("about:blank", "_blank");
    try {
      const r = await api.post(endpoint, { ...payload, opportunity_id: opp.id }, { headers });
      if (compact) { setResult({ ...payload, ...r.data }); onDone?.(r.data); return; }
      // Public applicant: go straight to WhatsApp (exactly as if they pressed "send approval message") — no extra screen.
      const link = whatsappLink(approvalMessage(payload));
      if (waTab) waTab.location.href = link; else window.location.href = link;
      toast.success(t.reg.successTitle, { description: t.reg.successSub });
      form.reset(); onDone?.(r.data);
    } catch (err) { waTab?.close(); toast.error(errorMessage(err, t.reg.error)); }
    finally { setLoading(false); }
  };

  if (result) {
    return (
      <div className="success-box" data-testid="registration-success">
        <span className="ring"><Check size={32} /></span>
        <h3>{t.reg.successTitle}</h3>
        <p className="muted" style={{ marginTop: 8 }}>{t.coord.autoApproved} <CopyCode code={result.access_code} testId="student-code" /></p>
      </div>
    );
  }

  return (
    <form className="form" onSubmit={submit} data-testid="registration-form">
      <StudentFields />
      {!compact && <div className="warn-box full" data-testid="pre-submit-note"><AlertTriangle size={17} /><span>{t.reg.preNote}</span></div>}
      <button className="btn btn-primary" type="submit" disabled={loading} data-testid="reg-submit-button">{loading ? <span className="loader" /> : compact ? t.reg.submit : <><MessageCircle size={18} />{t.reg.submitWa}</>}</button>
    </form>
  );
};

export const RegistrationModal = ({ opp, onClose }) => {
  const { t } = useLang();
  const isFull = opp.status !== "open" || opp.remaining_seats <= 0;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="registration-modal">
        <button className="modal-close" onClick={onClose} data-testid="registration-modal-close"><X size={18} /></button>
        {isFull ? (
          <div className="success-box" data-testid="registration-full"><h3>{t.reg.fullTitle}</h3><p className="muted" style={{ marginTop: 8 }}>{t.reg.fullSub}</p>
            <a className="btn btn-outline" style={{ marginTop: 16 }} href={TELEGRAM} target="_blank" rel="noreferrer"><Send size={16} />Telegram</a></div>
        ) : (<>
          <h2>{t.reg.details}</h2>
          <OpportunitySummary opp={opp} />
          <h2 style={{ marginTop: 22 }}>{t.reg.title}</h2>
          <p className="muted" style={{ fontSize: 14 }}>{t.reg.sub}</p>
          <RegistrationForm opp={opp} onDone={onClose} />
        </>)}
      </div>
    </div>
  );
};
